import React from "react";
import { Link } from "react-router-dom";
import type { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { formatPercentFromFraction } from "utils/formatters/PercentFormatters";
import type { RateCard } from "client/api-v2";
import { DataTable } from "components/DataTable";
import { MutedPlaceholder } from "components/MutedPlaceholder";
import { rateCardRoutes } from "../rate-card-routes";

interface RateCardListProps {
  rateCards: RateCard[];
  totalCount: number;
  loading?: boolean;
  page: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
}

const formatMoney = (value: number) => (Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`);

function summariseTerms(rateCard: RateCard): string {
  const unitPrices = rateCard.unitPrices.map(
    (price) => `${formatMoney(price.listPricePerUnit)}/${price.vector.toLowerCase()}`
  );
  const discount =
    rateCard.discountExponent === 0
      ? "no volume discount"
      : `V=${rateCard.discountExponent} over ${rateCard.discountThresholdUnits} units`;

  const overallDiscount =
    rateCard.overallDiscountFraction === 0
      ? []
      : [`${formatPercentFromFraction(rateCard.overallDiscountFraction)} off the total`];

  return [...unitPrices, discount, ...overallDiscount].join(" · ");
}

// Only fields in RateCardRepository's sort whitelist may be sortable — the API rejects the rest.
const columns: GridColDef<RateCard>[] = [
  {
    field: "code",
    headerName: "Code",
    minWidth: 140,
    renderCell: (params) => (
      <Link to={rateCardRoutes.detail(params.row.code).root} title="View rate card details">
        {params.row.code}
      </Link>
    ),
  },
  {
    field: "label",
    headerName: "Label",
    minWidth: 200,
    flex: 1,
    renderCell: (params) => (params.row.label ? params.row.label : <MutedPlaceholder>(no label)</MutedPlaceholder>),
  },
  {
    field: "currency",
    headerName: "Currency",
    minWidth: 100,
    sortable: false,
  },
  {
    field: "termsSummary",
    headerName: "Terms summary",
    minWidth: 320,
    flex: 2,
    sortable: false,
    valueGetter: (_, row) => summariseTerms(row),
  },
  {
    field: "fixedFees",
    headerName: "Fixed fees",
    minWidth: 100,
    sortable: false,
    valueGetter: (_, row) => (row.fixedFees.length > 0 ? "Yes" : "No"),
  },
];

export function RateCardList({
  rateCards,
  totalCount,
  loading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortModel,
  onSortModelChange,
}: RateCardListProps) {
  return (
    <DataTable
      rows={rateCards}
      columns={columns}
      rowCount={totalCount}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pagingMode="server"
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
}
